import { Textfield } from '@simplywallst/ui-core';
import {
  FormEvent,
  ReactNode,
  useDeferredValue,
  useEffect,
  useState,
} from 'react';
import useResizeObserver from 'use-resize-observer';
import { Popper } from './Popper';
import { isInputValid } from '../utils';
import { parseNumberSafe } from '../../../../Narrative/utils';
import {
  processRealNumberInput,
  roundNumber,
  stripFractionsExceedingDecimals,
} from '@/pages/Narrative/utils/number';

type Props = {
  name: string;
  value: number;
  precision: number;
  suffix: ReactNode;
  onChange: (value: number) => void;
  isValueValid: (value: number) => boolean;
  anchorEl: Element | null;
  boundaryEl: Element | null;
};

const INPUT_PADDING_BASE = 12;
const INPUT_PADDING_WITH_BORDER = INPUT_PADDING_BASE * 2 + 4;

export const SliderTextInput = ({
  name,
  value,
  precision,
  suffix,
  onChange,
  isValueValid,
  anchorEl,
  boundaryEl,
}: Props) => {
  const { ref: suffixRef, width: suffixWidth } = useResizeObserver();
  const { ref: inputMaskRef, width: inputMaskWidth } = useResizeObserver();
  const deferredInputMaskWidth = useDeferredValue(inputMaskWidth);
  const [input, setInput] = useState('');
  const inputNumber = parseNumberSafe(input, precision);
  const isValid = isInputValid(input) && isValueValid(inputNumber);

  const handleChangeManual = (e: FormEvent<HTMLInputElement>) => {
    const current = e.currentTarget.value;
    const newValue = stripFractionsExceedingDecimals(
      processRealNumberInput(current),
      precision
    );
    setInput(newValue);
  };
  const handleBlur = () => {
    if (!isValid) {
      setInput(roundNumber(value, precision).toString());
      return;
    }

    onChange(inputNumber);
  };

  useEffect(() => {
    setInput(roundNumber(value, precision).toString());
  }, [value, precision]);

  const targetSuffixWidth =
    suffix && suffixWidth ? suffixWidth + INPUT_PADDING_BASE : 0;
  const textInputWidth =
    (deferredInputMaskWidth ?? 30) +
    INPUT_PADDING_WITH_BORDER +
    targetSuffixWidth;

  return (
    <>
      <Popper
        anchorEl={anchorEl}
        boundaryEl={boundaryEl}
        updateKey={value}
        offsetX={0}
        offsetY={30}
      >
        <div
          className="max-w-[300px]"
          style={{
            width: `${textInputWidth}px`,
          }}
        >
          <Textfield
            name={name}
            styleType="compact"
            error={!isValid ? ' ' : undefined}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={input}
            onChange={handleChangeManual}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            postfix={<div ref={suffixRef}>{suffix}</div>}
          />
        </div>
      </Popper>
      <span
        className="text-xs inline-block invisible left-[-9999px] fixed"
        ref={inputMaskRef}
      >
        {input || '0'}
      </span>
    </>
  );
};
